import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { Empty, Mono, Page, PageHeader, Panel, Skeleton } from '@bounded/ui/primitives';
import { ConfirmationChip, ErrorNotice } from '@bounded/ui/state';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/operations')({ component: Operations });

function Operations() {
  const t = useT(); const m = t.operations; const f = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const operations = useQuery(q.operations());
  // Undelivered first (they need attention), then newest.
  const items = [...(operations.data?.items ?? [])].sort((a, b) => Number(a.delivered_at !== null) - Number(b.delivered_at !== null) || b.created_at - a.created_at);
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <p className="max-w-[72ch] rounded-[var(--radius-control)] border border-rule bg-surface px-3 py-2.5">{m.meaning}</p>
      {operations.error ? <ErrorNotice error={operations.error} onRetry={() => void operations.refetch()} /> : (
        <Panel>
          {!operations.data ? <div className="p-4"><Skeleton className="h-32" /></div> : !items.length ? <Empty title={m.empty} /> : (
            <ul>{items.map((o) => (
              <li key={o.id} className="grid gap-3 border-b border-rule px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                <div className="grid min-w-0 gap-0.5">
                  <p className="font-medium">{m.kinds[o.kind] ?? o.kind}</p>
                  <p className="flex flex-wrap gap-x-4 text-sm text-graphite">
                    <span>{m.target} {o.kind === 'device.revoke' ? <Link to="/devices/$deviceId" params={{ deviceId: o.target_id }} className="hover:underline"><Mono value={o.target_id} /></Link> : <Mono value={o.target_id} />}</span>
                    <span>{t.labels.id} <Mono value={o.id} /></span>
                    <span>{f.dateTime(o.created_at)}</span>
                  </p>
                </div>
                <div className="grid justify-items-start gap-2 sm:justify-items-end">
                  {o.delivered_at
                    // Plain text, not the device-confirmed "ink" chip: delivery is a cloud-side fact, not a device report.
                    ? <span className="text-sm text-graphite" title={f.dateTime(o.delivered_at)}>{m.delivered(f.ago(o.delivered_at))}</span>
                    : <ConfirmationChip state="pending">{m.waiting(o.attempts)}</ConfirmationChip>}
                </div>
                {!o.delivered_at && me.permissions.mutate && (
                  <div className="sm:col-span-2">
                    <ReasonAction label={m.retry} title={m.retryTitle} consequence={m.retryConsequence} payload={{ operation: o.id }}
                      run={(reason, key) => api.retryOperation(o.id, { reason }, key)}
                      onDone={() => Promise.all(['operations', 'audit', 'overview'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })))} />
                  </div>
                )}
              </li>
            ))}</ul>
          )}
        </Panel>
      )}
    </Page>
  );
}
